import React, { useCallback, useId } from "react";
import { shape, func } from "prop-types";
import { useParams, useLocation, useNavigate } from "react-router";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import queryString from "query-string";
const parseQueryString = queryString.parse;
import { urlEncode } from "../utils/url";

import { connectToState } from "../redux/connect-api";
import { tabKeys, SORTABLE_COLUMNS } from "../config";
import analytics from "shared/analytics";

const Search = ({ actions }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const id = `search-${useId()}`;
  const { setSort = null } = actions;
  const { search = "" } = location;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const searchString = e.target.search.value;
      const qs = searchString ? `?search=${urlEncode(searchString)}` : "";
      analytics.trackSearch(searchString);
      const activeTabKey = params?.activeTabKey ?? tabKeys[0];
      // If on last tab (report snapshot) use that key
      let tab = activeTabKey;
      if (activeTabKey === "compare-your-own-pricing") {
        tab = tabKeys[0];
      }
      if (searchString)
        // Do not sort on search (default to sorting by search score)
        setSort?.(null, "desc", "string");
      else {
        // Default to trade_name column if search is reset
        const fieldKey = "trade_name";
        setSort?.(fieldKey, "asc", SORTABLE_COLUMNS[fieldKey] || "string");
      }
      navigate(`/${tab}${qs}`);
    },
    [params?.activeTabKey, navigate, setSort],
  );

  const urlParams = parseQueryString(search);
  // Note difference between "search" as a react-router property, vs the URL param named "search":
  const searchString = urlParams.search || "";

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton aria-label="search" type="submit" sx={{ "&:focus": { background: "#ffffff50" } }}>
        <SearchIcon sx={{ color: "white" }} />
      </IconButton>
    </InputAdornment>
  );

  return (
    <form onSubmit={handleSubmit}>
      <FormControl
        sx={(theme) => ({
          position: "relative",
          borderRadius: theme.shape.borderRadius,
          marginLeft: 0,
          width: "100%",
          [theme.breakpoints.up("sm")]: {
            marginLeft: theme.spacing(1),
            width: "auto",
          },
        })}
      >
        <TextField
          id={id}
          name="search"
          label="Search by keyword"
          key={searchString}
          defaultValue={searchString}
          type="search"
          autoComplete="off"
          color="secondary"
          margin="dense"
          variant="outlined"
          sx={(theme) => ({
            marginLeft: theme.spacing(1),
            width: 350,
          })}
          slotProps={{
            input: {
              endAdornment,
              sx: {
                color: "white",
                "&.Mui-focused": {
                  backgroundColor: "rgba(255,255,255,.1)",
                  borderColor: "white",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "white !important",
                },
                "&:hover fieldset": {
                  borderColor: "white !important",
                },
                "&::-webkit-search-cancel-button": {
                  color: "white",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  color: "white",
                  borderColor: "rgba(255,255,255,.5)",
                  "&:focus": {
                    borderColor: "white",
                  },
                },
              },
            },

            inputLabel: {
              sx: {
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  color: "white",
                },
                "&.Mui-focused": {
                  color: "white !important",
                },
              },
            },
          }}
        />
      </FormControl>
    </form>
  );
};

Search.propTypes = {
  actions: shape({
    setSort: func,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  // only need access to actions
  sort: state.sort,
});

export default connectToState(mapStateToProps)(Search);
